import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import { Routes, Route, createRoutesFromElements } from 'react-router-dom';
import { io } from 'socket.io-client';

// Import background styles
import './styles/background.css';

// Components
import ModernHeader from './components/ModernHeader';
import Footer from './components/Footer';
import Toast from './components/Toast';
import Spinner from './components/Spinner';

// Import configuration
import config from './config';

// Lazy-loaded pages (code splitting — only loads JS when route is visited)
const Home = React.lazy(() => import('./pages/Home'));
const Block = React.lazy(() => import('./pages/Block'));
const Transaction = React.lazy(() => import('./pages/Transaction'));
const Address = React.lazy(() => import('./pages/Address'));
const BlockList = React.lazy(() => import('./pages/BlockList'));
const TransactionList = React.lazy(() => import('./pages/TransactionList'));
const Status = React.lazy(() => import('./pages/Status'));
const Charts = React.lazy(() => import('./pages/Charts'));
const Search = React.lazy(() => import('./pages/Search'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const WealthDistribution = React.lazy(() => import('./pages/WealthDistribution'));
const Developers = React.lazy(() => import('./pages/Developers'));
const PoolOperators = React.lazy(() => import('./pages/PoolOperators'));

// Context
import { SocketContext } from './contexts/SocketContext';
import { ToastContext } from './contexts/ToastContext';

// Import background manager
import backgroundManager from './utils/backgroundManager';

function App({ skipHeader = false }) {
  // Initialize state and refs
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [toast, setToast] = useState(null);

  // Define showToast callback
  const showToast = useCallback((message, type = 'info', duration = 5000) => {
    setToast({ message, type, duration });

    const timer = setTimeout(() => {
      setToast(null);
    }, duration);

    return () => clearTimeout(timer);
  }, []);

  // Create toast context value
  const toastContextValue = useMemo(() => ({ showToast }), [showToast]);

  // Initialize background animation
  useEffect(() => {
    // Initialize with the primary style
    backgroundManager.init('primary');

    // Clean up animation on unmount
    return () => {
      backgroundManager.stop();
    };
  }, []);

  // Initialize socket connection with better error handling and reconnection logic
  useEffect(() => {
    if (socketRef.current) return;

    // Create socket with improved options from config
    socketRef.current = io(config.socket.url, {
      ...config.socket.options,
      forceNew: true
    });

    const currentSocket = socketRef.current;

    // Connection established
    currentSocket.on('connect', () => {
      console.log('Connected to WebSocket server');
      setIsConnected(true);

      // Subscribe to channels
      currentSocket.emit('subscribe', 'blocks');
      currentSocket.emit('subscribe', 'transactions');
      currentSocket.emit('subscribe', 'general');
    });

    // Connection error
    currentSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Connection closed
    currentSocket.on('disconnect', (reason) => {
      console.log(`Disconnected from WebSocket server: ${reason}`);
      setIsConnected(false);

      // If the server closed the connection, try to reconnect immediately
      if (reason === 'io server disconnect') {
        currentSocket.connect();
      }
    });

    // Receive heartbeat to confirm connection is alive
    currentSocket.on('heartbeat', (data) => {
      const latency = Date.now() - data.timestamp;
      if (latency > 5000) {
        console.warn(`High socket latency detected: ${latency}ms`);
      }
    });

    // Notification handling
    currentSocket.on('notification', (notification) => {
      if (notification.type === 'block') {
        showToast(`New block #${notification.data.height} mined`, 'info');
      }
    });

    // Reconnect success
    currentSocket.on('reconnect', (attemptNumber) => {
      console.log(`Reconnected to WebSocket server after ${attemptNumber} attempts`);
      setIsConnected(true);

      // Re-subscribe to channels
      currentSocket.emit('subscribe', 'blocks');
      currentSocket.emit('subscribe', 'transactions');
      currentSocket.emit('subscribe', 'general');
    });

    // Reconnection attempt
    currentSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`Attempting to reconnect to WebSocket server (attempt ${attemptNumber})`);
    });

    // Reconnection error
    currentSocket.on('reconnect_error', (error) => {
      console.error('WebSocket reconnection error:', error);
    });

    // Reconnection failed
    currentSocket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed, giving up');
      showToast('Real-time updates unavailable. Please refresh the page.', 'error', 10000);
    });

    // Cleanup on unmount
    return () => {
      if (currentSocket) {
        // Unsubscribe from all channels first
        currentSocket.emit('unsubscribe', 'blocks');
        currentSocket.emit('unsubscribe', 'transactions');
        currentSocket.emit('unsubscribe', 'general');

        // Then disconnect
        currentSocket.disconnect();
        socketRef.current = null;
      }
    };
  }, [showToast]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Background animation container */}
      <div id="bg-animation-container" className="bg-animation-container"></div>

      {/* Only include the header if skipHeader is false */}
      {!skipHeader && <ModernHeader />}

      {/* Main content with WebSocket context */}
      <SocketContext.Provider value={socketRef.current}>
        <ToastContext.Provider value={toastContextValue}>
          <main className="flex-grow py-4 sm:py-6 md:py-8 content-container">
            <Suspense fallback={<Spinner />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/blocks" element={<BlockList />} />
                <Route path="/blocks/:hash" element={<Block />} />
                <Route path="/transactions" element={<TransactionList />} />
                <Route path="/tx/:txid" element={<Transaction />} />
                <Route path="/address/:address" element={<Address />} />
                <Route path="/stats" element={<Status />} />
                <Route path="/charts" element={<Charts />} />
                <Route path="/wealth-distribution" element={<WealthDistribution />} />
                <Route path="/search" element={<Search />} />
                <Route path="/developers" element={<Developers />} />
                <Route path="/pool-operators" element={<PoolOperators />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          {toast && <Toast message={toast.message} type={toast.type} />}
        </ToastContext.Provider>
      </SocketContext.Provider>
    </div>
  );
}

export default App;
